import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://www.williamsdiversified.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-image.jpg`;
const COMPANY_NAME = 'Williams Diversified LLC';

// Page-specific SEO data
const pageSeoData = {
  '/': {
    title: 'Williams Diversified LLC | Mission-Critical Construction & Facility Services',
    description: 'Williams Diversified delivers mission-critical construction, facility maintenance, and emergency response services nationwide. 24/7 availability. Government & commercial.',
  },
  '/about': {
    title: 'About Us | Williams Diversified LLC',
    description: 'Learn about Williams Diversified LLC - your trusted partner for construction, facility maintenance, and emergency response services nationwide.',
  },
  '/services': {
    title: 'Our Services | Williams Diversified LLC',
    description: 'Comprehensive construction and facility services: civil construction, project management, demolition, plumbing, electrical, interior finishes, and more.',
  },
  '/industries': {
    title: 'Industries We Serve | Williams Diversified LLC',
    description: 'Serving government, defense, healthcare, commercial real estate, retail, hospitality, infrastructure, industrial, education, energy, and more.',
  },
  '/government-contracting': {
    title: 'Government Contracting | Williams Diversified LLC',
    description: 'Federal, state, and municipal contracting services. Base operations support, emergency response, and mission-critical facility services.',
  },
  '/contact': {
    title: 'Contact Us | Williams Diversified LLC',
    description: 'Contact Williams Diversified for your construction and facility needs. 24/7 emergency response. Call (918) 493-3418 or email inquire@williamsdiversified.com.',
  },
  '/careers': {
    title: 'Careers | Williams Diversified LLC',
    description: 'Join the Williams Diversified team. Explore career opportunities in construction, facility maintenance, and project management.',
  },
  '/certifications': {
    title: 'Certifications & Credentials | Williams Diversified LLC',
    description: 'Licensed, insured, and OSHA certified. View our certifications and credentials for government and commercial contracting.',
  },
  '/facility-maintenance': {
    title: 'Facility Maintenance Services | Williams Diversified LLC',
    description: 'Comprehensive facility maintenance services for commercial, government, and industrial properties. Preventive and emergency maintenance.',
  },
  '/request-quote': {
    title: 'Request a Quote | Williams Diversified LLC',
    description: 'Get a free quote for your construction or facility maintenance project. Contact Williams Diversified today.',
  },
  '/testimonials': {
    title: 'Client Testimonials | Williams Diversified LLC',
    description: 'Read what our clients say about Williams Diversified construction and facility services.',
  },
  '/projects': {
    title: 'Our Projects | Williams Diversified LLC',
    description: 'View our portfolio of completed construction and facility projects across government, commercial, and industrial sectors.',
  },
  '/portal': {
    title: 'Client Portal | Williams Diversified LLC',
    description: 'Access the Williams Diversified client portal for project management and communication.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Williams Diversified LLC',
    description: 'Williams Diversified LLC privacy policy and data protection practices.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Williams Diversified LLC',
    description: 'Williams Diversified LLC terms of service and conditions of use.',
  },
  '/faq': {
    title: 'FAQ | Williams Diversified LLC',
    description: 'Frequently asked questions about Williams Diversified construction and facility services.',
  },
};

export function SEOHead() {
  const location = useLocation();
  const pathname = location.pathname;
  
  useEffect(() => {
    // Get SEO data for current page or use defaults
    const seoData = pageSeoData[pathname] || {
      title: `${COMPANY_NAME} | Mission-Critical Solutions`,
      description: 'Williams Diversified delivers mission-critical construction, facility maintenance, and emergency response services nationwide.',
    };
    
    const canonicalUrl = `${BASE_URL}${pathname === '/' ? '' : pathname}`;
    
    // Update document title
    document.title = seoData.title;
    
    // Helper function to update or create meta tags
    const updateMetaTag = (selector, content, attr = 'content') => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        if (selector.includes('property=')) {
          tag.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          tag.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute(attr, content);
    };
    
    // Update meta description
    updateMetaTag('meta[name="description"]', seoData.description);
    
    // Open Graph tags
    updateMetaTag('meta[property="og:title"]', seoData.title);
    updateMetaTag('meta[property="og:description"]', seoData.description);
    updateMetaTag('meta[property="og:url"]', canonicalUrl);
    updateMetaTag('meta[property="og:type"]', 'website');
    updateMetaTag('meta[property="og:image"]', DEFAULT_OG_IMAGE);
    updateMetaTag('meta[property="og:site_name"]', COMPANY_NAME);
    
    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', seoData.title);
    updateMetaTag('meta[name="twitter:description"]', seoData.description);
    updateMetaTag('meta[name="twitter:image"]', DEFAULT_OG_IMAGE);
    
    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    
  }, [pathname]);
  
  return null; // This component doesn't render anything
}

export default SEOHead;
